/**
 * Downscale an image in the browser, before it is uploaded.
 *
 * **Why the client does this.** A journal day carries a dozen phone photographs
 * at three or four megabytes each, and a grid that loads the originals is tens
 * of megabytes for pictures rendered at 160 pixels — which is the difference
 * between a screen that can be demonstrated and one that cannot. The obvious
 * alternative is resizing server-side, and the API has no image library to do
 * it with: `sharp` was deliberately rejected (`document-ai/0001` §2.10) rather
 * than carry a second native binary into an Alpine image that already fought
 * one. A canvas is already in the page.
 *
 * **It is a convenience, never the source of truth.** The original is what gets
 * stored, described, cited and downloaded; this is only what the grid shows.
 * So every failure path returns null — an unreadable file, a format the canvas
 * cannot decode, a tainted canvas — and the caller simply uploads without one
 * and lets the grid fall back to the original.
 */

/** Long edge of the downscale. Twice a 160px grid cell, for retina. */
const MAX_EDGE = 480;

/** JPEG quality. Visually indistinguishable at thumbnail size, ~20 kB. */
const QUALITY = 0.7;

export type Thumbnail = {
  blob: Blob;
  /** The *original's* pixel dimensions, read for free while decoding. */
  widthPx: number;
  heightPx: number;
};

/**
 * A JPEG downscale of `file`, plus the original's dimensions — or null.
 *
 * The dimensions come back even though the caller could not otherwise get them
 * without decoding the image a second time, and the grid needs them to reserve
 * the right aspect ratio before any bytes arrive.
 */
export async function makeThumbnail(file: File): Promise<Thumbnail | null> {
  let bitmap: ImageBitmap | null = null;
  try {
    // `createImageBitmap` decodes off the main thread, so dropping twelve
    // photographs at once does not lock the page while they are read.
    bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    if (!width || !height) return null;

    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      // Always JPEG, whatever went in: a downscaled PNG photograph is larger
      // than the JPEG it came from, which would defeat the whole point.
      canvas.toBlob(resolve, "image/jpeg", QUALITY),
    );
    return blob ? { blob, widthPx: width, heightPx: height } : null;
  } catch {
    // A format the browser cannot decode — HEIC is the one that matters, and it
    // is rejected upstream with a message that says what to do about it. There
    // is nothing useful to report here: the upload proceeds without a
    // thumbnail, which is a slower grid and not a lost photograph.
    return null;
  } finally {
    bitmap?.close();
  }
}
